<template>
  <v-container fluid class="pa-4 pa-md-6">
    <!-- ============================ ENCABEZADO ============================ -->
    <div class="d-flex align-center flex-wrap mb-1">
      <v-icon color="primary" class="mr-2">mdi-shield-account</v-icon>
      <h1 class="text-h5 font-weight-bold">Administración Y2D</h1>
      <v-spacer></v-spacer>
      <v-btn text small class="rounded-lg" :loading="loading || loadingUsers || loadingPending"
        @click="reloadAll">
        <v-icon left small>mdi-refresh</v-icon> Actualizar
      </v-btn>
    </div>
    <p class="text-body-2 grey--text text--darken-1 mb-4">
      Empresas, suscripciones, usuarios y pagos por validar. Solo visible para administradores.
    </p>

    <!-- ============================ TARJETAS ============================ -->
    <v-row class="mb-1">
      <v-col v-for="card in statCards" :key="card.label" cols="6" md="4" lg="2">
        <v-card outlined class="rounded-lg h-100" :class="{ 'card-clickable': card.tab !== undefined }"
          @click="card.tab !== undefined ? goTab(card.tab, card.filter) : null">
          <v-card-text class="py-3">
            <div class="d-flex align-center">
              <v-icon small :color="card.color" class="mr-1">{{ card.icon }}</v-icon>
              <span class="text-caption grey--text text--darken-1">{{ card.label }}</span>
            </div>
            <div class="text-h5 font-weight-bold mt-1" :class="card.color + '--text'">{{ card.value }}</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Aviso: plata esperando -->
    <v-alert v-if="pendingCount > 0" type="warning" text dense border="left" class="rounded-lg mt-2 mb-4">
      Hay <strong>{{ pendingCount }}</strong>
      {{ pendingCount === 1 ? 'pago esperando validación' : 'pagos esperando validación' }}.
      <a class="font-weight-bold" @click="goTab(2)">Revisarlos ahora</a>
    </v-alert>

    <v-tabs v-model="tab" class="mb-4">
      <v-tab>Empresas</v-tab>
      <v-tab>Usuarios</v-tab>
      <v-tab>
        Pagos por validar
        <v-chip v-if="pendingCount > 0" x-small dark color="orange" class="ml-2">{{ pendingCount }}</v-chip>
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab">
      <!-- ========================== EMPRESAS ========================== -->
      <v-tab-item>
        <div class="d-flex align-center flex-wrap mb-3">
          <v-text-field v-model="search" prepend-inner-icon="mdi-magnify"
            label="Buscar por empresa, dueño o correo" outlined dense clearable hide-details
            class="mr-3 mb-2" style="max-width: 380px;"></v-text-field>
          <v-chip-group v-model="bizFilter" mandatory active-class="primary--text" class="mb-2">
            <v-chip v-for="f in bizFilters" :key="f.value" :value="f.value" small outlined>
              {{ f.label }}
              <span v-if="bizCounts[f.value] !== undefined" class="ml-1 grey--text">({{ bizCounts[f.value] }})</span>
            </v-chip>
          </v-chip-group>
        </div>

        <v-card outlined class="rounded-lg">
          <v-data-table :headers="bizHeaders" :items="filteredBusinesses" :loading="loading"
            loading-text="Cargando…" :no-data-text="bizEmptyText"
            :items-per-page="15" :footer-props="{ 'items-per-page-options': [15, 30, 50, -1] }">

            <template v-slot:[`item.business_name`]="{ item }">
              <div class="font-weight-medium d-flex align-center">
                {{ item.business_name }}
                <v-chip v-if="item.is_empty" x-small outlined color="grey" class="ml-2">vacía</v-chip>
              </div>
              <div class="text-caption grey--text">
                {{ item.owner.name }} · {{ item.owner.email }}
              </div>
            </template>

            <template v-slot:[`item.content`]="{ item }">
              <div v-if="item.customers_count === null" class="text-caption grey--text">—</div>
              <div v-else class="text-caption">
                <span :class="item.customers_count ? '' : 'grey--text'">{{ item.customers_count }} clientes</span>
                ·
                <span :class="item.products_count ? '' : 'grey--text'">{{ item.products_count }} prod.</span>
                ·
                <span :class="item.projections_count ? '' : 'grey--text'">{{ item.projections_count }} casos</span>
                <div class="grey--text">{{ item.last_activity ? 'usada ' + fmtDate(item.last_activity) : 'sin actividad' }}</div>
              </div>
            </template>

            <template v-slot:[`item.plan`]="{ item }">
              <span v-if="item.plan">{{ item.plan }}</span>
              <span v-else class="grey--text">sin plan</span>
            </template>

            <template v-slot:[`item.status_display`]="{ item }">
              <v-chip small dark :color="statusColor(item.status)">{{ item.status_display }}</v-chip>
            </template>

            <template v-slot:[`item.days_remaining`]="{ item }">
              <span v-if="!item.subscription_id" class="grey--text">—</span>
              <span v-else-if="item.days_remaining === null">∞</span>
              <span v-else-if="item.days_remaining < 0" class="red--text font-weight-bold">
                vencido hace {{ -item.days_remaining }} d
              </span>
              <span v-else class="font-weight-bold"
                :class="(item.days_remaining > 7 ? 'green' : 'orange') + '--text'">
                {{ item.days_remaining }} d
              </span>
            </template>

            <template v-slot:[`item.actions`]="{ item }">
              <v-menu offset-y left>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn small outlined color="primary" v-bind="attrs" v-on="on">
                    Acciones <v-icon right small>mdi-menu-down</v-icon>
                  </v-btn>
                </template>
                <v-list dense>
                  <!-- Sin suscripción: antes no había NINGUNA acción posible -->
                  <template v-if="!item.subscription_id">
                    <v-list-item @click="openPlanDialog(item, true)">
                      <v-list-item-icon class="mr-2"><v-icon color="primary">mdi-plus-circle-outline</v-icon></v-list-item-icon>
                      <v-list-item-title>Asignar plan y crear suscripción</v-list-item-title>
                    </v-list-item>
                  </template>
                  <template v-else>
                    <v-list-item @click="confirmAction('markPaid', item)">
                      <v-list-item-icon class="mr-2"><v-icon color="green">mdi-cash-check</v-icon></v-list-item-icon>
                      <v-list-item-title>Marcar pagado (+30 días)</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="confirmAction('extend', item)">
                      <v-list-item-icon class="mr-2"><v-icon color="blue">mdi-calendar-plus</v-icon></v-list-item-icon>
                      <v-list-item-title>Extender 30 días</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="openPlanDialog(item, false)">
                      <v-list-item-icon class="mr-2"><v-icon>mdi-tag-outline</v-icon></v-list-item-icon>
                      <v-list-item-title>Cambiar plan</v-list-item-title>
                    </v-list-item>
                    <v-divider></v-divider>
                    <v-list-item v-if="item.is_active" @click="confirmAction('suspend', item)">
                      <v-list-item-icon class="mr-2"><v-icon color="orange">mdi-cancel</v-icon></v-list-item-icon>
                      <v-list-item-title>Suspender acceso</v-list-item-title>
                    </v-list-item>
                    <v-list-item v-else @click="confirmAction('activate', item)">
                      <v-list-item-icon class="mr-2"><v-icon color="green">mdi-check-circle</v-icon></v-list-item-icon>
                      <v-list-item-title>Reactivar</v-list-item-title>
                    </v-list-item>
                  </template>
                  <v-divider></v-divider>
                  <v-list-item @click="openDeleteDialog(item)">
                    <v-list-item-icon class="mr-2"><v-icon color="red">mdi-delete-forever</v-icon></v-list-item-icon>
                    <v-list-item-title class="red--text">Eliminar empresa…</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
          </v-data-table>
        </v-card>
      </v-tab-item>

      <!-- ========================== USUARIOS ========================== -->
      <v-tab-item>
        <div class="d-flex align-center flex-wrap mb-3">
          <v-text-field v-model="userSearch" prepend-inner-icon="mdi-magnify" label="Buscar por nombre o correo"
            outlined dense clearable hide-details class="mr-3 mb-2" style="max-width: 340px;"></v-text-field>
          <v-chip-group v-model="userFilter" mandatory active-class="primary--text" class="mb-2">
            <v-chip v-for="f in userFilters" :key="f.value" :value="f.value" small outlined>{{ f.label }}</v-chip>
          </v-chip-group>
          <v-spacer></v-spacer>
          <v-btn color="primary" class="rounded-lg mb-2" @click="openCreateUser">
            <v-icon left>mdi-account-plus</v-icon> Crear usuario
          </v-btn>
        </div>

        <v-card outlined class="rounded-lg">
          <v-data-table :headers="userHeaders" :items="filteredUsers" :loading="loadingUsers"
            loading-text="Cargando…" no-data-text="No hay usuarios que coincidan"
            :items-per-page="15" :footer-props="{ 'items-per-page-options': [15, 30, 50, -1] }">

            <template v-slot:[`item.full_name`]="{ item }">
              <div class="font-weight-medium">{{ item.full_name || '(sin nombre)' }}</div>
              <div class="text-caption grey--text">{{ item.email }}</div>
            </template>

            <template v-slot:[`item.businesses`]="{ item }">
              <div v-if="!item.businesses || !item.businesses.length" class="text-caption grey--text">
                sin empresa
              </div>
              <div v-else>
                <v-chip v-for="b in item.businesses" :key="b.id" x-small outlined class="mr-1 mb-1"
                  :color="b.is_owner ? 'primary' : 'grey'">
                  {{ b.name }} · {{ b.role }}
                </v-chip>
              </div>
            </template>

            <template v-slot:[`item.is_active`]="{ item }">
              <v-chip small dark :color="item.is_active ? 'green' : 'grey'">
                {{ item.is_active ? 'Activo' : 'Inactivo' }}
              </v-chip>
              <v-chip v-if="item.is_staff" small dark color="primary" class="ml-1">Admin</v-chip>
            </template>

            <template v-slot:[`item.date_joined`]="{ item }">{{ fmtDate(item.date_joined) }}</template>
            <template v-slot:[`item.last_login`]="{ item }">
              <span :class="item.last_login ? '' : 'grey--text'">{{ fmtDate(item.last_login) }}</span>
            </template>

            <template v-slot:[`item.uactions`]="{ item }">
              <v-menu offset-y left>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn small outlined color="primary" v-bind="attrs" v-on="on">
                    Acciones <v-icon right small>mdi-menu-down</v-icon>
                  </v-btn>
                </template>
                <v-list dense>
                  <v-list-item @click="openEditUser(item)">
                    <v-list-item-icon class="mr-2"><v-icon>mdi-pencil</v-icon></v-list-item-icon>
                    <v-list-item-title>Editar datos</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="doResetPassword(item)">
                    <v-list-item-icon class="mr-2"><v-icon color="orange">mdi-key-variant</v-icon></v-list-item-icon>
                    <v-list-item-title>Resetear contraseña</v-list-item-title>
                  </v-list-item>
                  <v-divider></v-divider>
                  <v-list-item v-if="!item.is_staff" @click="confirmStaff(item, true)">
                    <v-list-item-icon class="mr-2"><v-icon color="primary">mdi-shield-plus</v-icon></v-list-item-icon>
                    <v-list-item-title>Dar acceso de administrador</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-else :disabled="esMiUsuario(item)" @click="confirmStaff(item, false)">
                    <v-list-item-icon class="mr-2"><v-icon color="orange">mdi-shield-off</v-icon></v-list-item-icon>
                    <v-list-item-title>
                      Quitar acceso de administrador
                      <span v-if="esMiUsuario(item)" class="text-caption grey--text">(eres tú)</span>
                    </v-list-item-title>
                  </v-list-item>
                  <v-divider></v-divider>
                  <v-list-item v-if="item.is_active" :disabled="esMiUsuario(item)" @click="confirmToggleActive(item, false)">
                    <v-list-item-icon class="mr-2"><v-icon color="red">mdi-account-off</v-icon></v-list-item-icon>
                    <v-list-item-title>
                      Desactivar
                      <span v-if="esMiUsuario(item)" class="text-caption grey--text">(eres tú)</span>
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item v-else @click="confirmToggleActive(item, true)">
                    <v-list-item-icon class="mr-2"><v-icon color="green">mdi-account-check</v-icon></v-list-item-icon>
                    <v-list-item-title>Activar</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
          </v-data-table>
        </v-card>

        <p class="text-caption grey--text mt-3 mb-0">
          Los usuarios no se borran, se desactivan: la empresa cuelga del dueño en cascada, así que
          eliminar al dueño se llevaría su empresa con todos sus datos. Para limpiar datos, borra la
          empresa desde la pestaña Empresas.
        </p>
      </v-tab-item>

      <!-- ====================== PAGOS POR VALIDAR ====================== -->
      <v-tab-item>
        <div class="d-flex align-center flex-wrap mb-3">
          <v-switch v-model="pendingShowAll" label="Ver también los ya resueltos" dense hide-details
            class="mt-0 mr-4" @change="loadPending"></v-switch>
          <v-spacer></v-spacer>
        </div>

        <v-card outlined class="rounded-lg">
          <v-data-table :headers="pendingHeaders" :items="pending" :loading="loadingPending"
            loading-text="Cargando…"
            no-data-text="No hay pagos esperando validación. Todo al día."
            :items-per-page="15">
            <template v-slot:[`item.who`]="{ item }">
              <div class="font-weight-medium">{{ item.business_name || '(sin empresa)' }}</div>
              <div class="text-caption grey--text">{{ item.first_name }} · {{ item.email }}</div>
            </template>
            <template v-slot:[`item.amount_clp`]="{ item }">
              <span class="font-weight-bold">{{ formatClp(item.amount_clp) }}</span>
              <div class="text-caption grey--text">{{ item.plan || 'sin plan' }}</div>
            </template>
            <template v-slot:[`item.payment_method_display`]="{ item }">
              {{ item.payment_method_display }}
              <div v-if="item.transfer_reference" class="text-caption grey--text">
                ref: {{ item.transfer_reference }}
              </div>
            </template>
            <template v-slot:[`item.created_at`]="{ item }">{{ fmtDate(item.created_at) }}</template>
            <template v-slot:[`item.status_display`]="{ item }">
              <v-chip small dark :color="pendingColor(item.status)">{{ item.status_display }}</v-chip>
              <div v-if="item.reviewed_by" class="text-caption grey--text">
                por {{ item.reviewed_by }}
              </div>
            </template>
            <template v-slot:[`item.pactions`]="{ item }">
              <div v-if="item.status === 'AWAITING_TRANSFER'">
                <v-btn small color="green" dark class="mr-1 rounded-lg" :loading="acting"
                  @click="confirmApprove(item)">
                  <v-icon left small>mdi-check</v-icon> Validar
                </v-btn>
                <v-btn small outlined color="red" class="rounded-lg" @click="openRejectDialog(item)">
                  Rechazar
                </v-btn>
              </div>
              <span v-else class="text-caption grey--text">resuelto</span>
            </template>
          </v-data-table>
        </v-card>

        <p class="text-caption grey--text mt-3 mb-0">
          Validar crea o extiende la cuenta y activa la suscripción. Hazlo solo después de ver la
          plata en la cuenta bancaria.
        </p>
      </v-tab-item>
    </v-tabs-items>

    <!-- ===================== DIÁLOGO PLAN ===================== -->
    <v-dialog v-model="planDialog" max-width="440">
      <v-card>
        <v-card-title class="text-h6">
          {{ planIsNew ? 'Asignar plan y crear suscripción' : 'Cambiar plan' }}
        </v-card-title>
        <v-card-text>
          <div class="mb-3 text-body-2 font-weight-medium" v-if="selected">{{ selected.business_name }}</div>
          <v-select v-model="selectedPlanId" :items="plans" item-value="id" label="Plan" outlined dense
            :item-text="planLabel"></v-select>
          <div v-if="planIsNew" class="text-caption grey--text">
            Se crea en estado de prueba, con la duración del plan. No registra un pago.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="planDialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="acting" :disabled="!selectedPlanId" @click="confirmSetPlan">
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ===================== DIÁLOGO BORRAR EMPRESA ===================== -->
    <v-dialog v-model="deleteDialog" max-width="520" persistent>
      <v-card v-if="selected">
        <v-card-title class="text-h6 red--text">Eliminar empresa</v-card-title>
        <v-card-text>
          <v-alert type="error" text dense border="left" class="rounded-lg">
            Esto es <strong>irreversible</strong> y borra en cascada todo el contenido de la empresa.
            No hay papelera.
          </v-alert>
          <div class="text-body-2 mb-2">Se va a borrar de <strong>{{ selected.business_name }}</strong>:</div>
          <ul class="text-body-2 mb-4">
            <li>{{ selected.customers_count || 0 }} clientes</li>
            <li>{{ selected.products_count || 0 }} productos o servicios</li>
            <li>{{ selected.projections_count || 0 }} casos, con su Mix Óptimo y planes de venta</li>
          </ul>
          <div class="text-body-2 mb-2">
            Para confirmar, escribe el nombre exacto: <code>{{ selected.business_name }}</code>
          </div>
          <v-text-field v-model="deleteConfirmName" outlined dense hide-details
            placeholder="Nombre de la empresa" autocomplete="off"></v-text-field>
          <div v-if="deleteError" class="red--text text-body-2 mt-2">{{ deleteError }}</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false" :disabled="acting">Cancelar</v-btn>
          <v-btn color="red" dark :loading="acting" :disabled="deleteConfirmName !== selected.business_name"
            @click="confirmDelete">
            Eliminar definitivamente
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ===================== DIÁLOGO RECHAZAR PAGO ===================== -->
    <v-dialog v-model="rejectDialog" max-width="460">
      <v-card>
        <v-card-title class="text-h6">Rechazar pago</v-card-title>
        <v-card-text>
          <div class="text-body-2 mb-3" v-if="selectedPending">
            {{ selectedPending.business_name }} · {{ formatClp(selectedPending.amount_clp) }}
          </div>
          <v-textarea v-model="rejectReason" label="Motivo (queda registrado)" outlined dense rows="3"
            hide-details></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="rejectDialog = false">Cancelar</v-btn>
          <v-btn color="red" dark :loading="acting" @click="confirmReject">Rechazar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ===================== DIÁLOGO USUARIO ===================== -->
    <v-dialog v-model="userDialog" max-width="460">
      <v-card>
        <v-card-title class="text-h6">{{ editingUser ? 'Editar usuario' : 'Crear usuario' }}</v-card-title>
        <v-card-text>
          <v-text-field v-model="userForm.first_name" label="Nombre" outlined dense></v-text-field>
          <v-text-field v-model="userForm.last_name" label="Apellido" outlined dense></v-text-field>
          <v-text-field v-model="userForm.email" label="Correo" outlined dense
            hint="El correo es también el usuario para entrar" persistent-hint class="mb-3"></v-text-field>
          <v-text-field v-if="!editingUser" v-model="userForm.business_name"
            label="Empresa (opcional)" outlined dense
            hint="Si la dejas vacía, el usuario queda sin empresa y no podrá crear una desde la app"
            persistent-hint></v-text-field>
          <div v-if="userError" class="red--text text-body-2 mt-3">{{ userError }}</div>
          <div v-if="!editingUser" class="text-caption grey--text mt-3">
            Se genera una contraseña y se muestra una sola vez.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="userDialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="acting" @click="saveUser">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ===================== DIÁLOGO CONTRASEÑA ===================== -->
    <v-dialog v-model="pwDialog" max-width="440" persistent>
      <v-card>
        <v-card-title class="text-h6">Contraseña generada</v-card-title>
        <v-card-text>
          <p class="text-body-2 grey--text">
            Entrégasela al usuario por un canal seguro. Por seguridad no se vuelve a mostrar.
          </p>
          <div class="text-caption grey--text">Usuario</div>
          <div class="font-weight-medium mb-2">{{ pwData.email }}</div>
          <div class="text-caption grey--text">Contraseña</div>
          <div class="d-flex align-center">
            <div class="font-weight-medium" style="font-family: monospace; font-size: 1.15rem;">
              {{ pwData.password }}
            </div>
            <v-spacer></v-spacer>
            <v-btn small icon @click="copyPw" title="Copiar"><v-icon small>mdi-content-copy</v-icon></v-btn>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="pwDialog = false">Listo</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <CustomConfirmDialog ref="confirm" />
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import CustomConfirmDialog from '@/components/common/dialogs/CustomConfirmDialog.vue'

export default {
  name: 'AdminView',
  components: { CustomConfirmDialog },
  data() {
    return {
      tab: 0,
      loading: false,
      loadingUsers: false,
      loadingPending: false,
      acting: false,

      search: '',
      bizFilter: 'all',
      userSearch: '',
      userFilter: 'all',

      businesses: [],
      users: [],
      pending: [],
      pendingShowAll: false,
      plans: [],
      stats: {},

      selected: null,
      selectedPending: null,

      planDialog: false,
      planIsNew: false,
      selectedPlanId: null,

      deleteDialog: false,
      deleteConfirmName: '',
      deleteError: '',

      rejectDialog: false,
      rejectReason: '',

      userDialog: false,
      editingUser: null,
      userForm: { first_name: '', last_name: '', email: '', business_name: '' },
      userError: '',
      pwDialog: false,
      pwData: { email: '', password: '' },

      bizFilters: [
        { label: 'Todas', value: 'all' },
        { label: 'Pagando', value: 'active' },
        { label: 'En prueba', value: 'trial' },
        { label: 'Vencidas', value: 'expired' },
        { label: 'Sin suscripción', value: 'nosub' },
        { label: 'Vacías', value: 'empty' },
      ],
      userFilters: [
        { label: 'Todos', value: 'all' },
        { label: 'Activos', value: 'active' },
        { label: 'Inactivos', value: 'inactive' },
        { label: 'Administradores', value: 'staff' },
        { label: 'Sin empresa', value: 'noBusiness' },
      ],
      bizHeaders: [
        { text: 'Empresa y dueño', value: 'business_name' },
        { text: 'Contenido', value: 'content', sortable: false },
        { text: 'Plan', value: 'plan' },
        { text: 'Estado', value: 'status_display' },
        { text: 'Vigencia', value: 'days_remaining' },
        { text: '', value: 'actions', sortable: false, align: 'end' },
      ],
      userHeaders: [
        { text: 'Usuario', value: 'full_name' },
        { text: 'Empresas', value: 'businesses', sortable: false },
        { text: 'Estado', value: 'is_active' },
        { text: 'Se registró', value: 'date_joined' },
        { text: 'Último acceso', value: 'last_login' },
        { text: '', value: 'uactions', sortable: false, align: 'end' },
      ],
      pendingHeaders: [
        { text: 'Empresa y contacto', value: 'who' },
        { text: 'Monto', value: 'amount_clp' },
        { text: 'Medio', value: 'payment_method_display' },
        { text: 'Fecha', value: 'created_at' },
        { text: 'Estado', value: 'status_display' },
        { text: '', value: 'pactions', sortable: false, align: 'end' },
      ],
    }
  },
  computed: {
    ...mapGetters('auth', ['user']),
    myUserId() { return this.user && this.user.id },
    // Fallback por correo: las sesiones abiertas antes de que /auth/me devolviera
    // `id` tienen el user en localStorage sin id, y sin esto el panel dejaría a un
    // admin desactivarse a sí mismo (el backend lo rechaza, pero es mejor que la
    // opción ni aparezca habilitada).
    myEmail() { return ((this.user && this.user.email) || '').toLowerCase() },
    pendingCount() { return this.stats.pending_transfers || 0 },

    statCards() {
      const s = this.stats
      return [
        { label: 'Empresas', value: s.total_businesses ?? 0, color: 'primary', icon: 'mdi-domain', tab: 0, filter: 'all' },
        { label: 'Pagando', value: s.active ?? 0, color: 'green', icon: 'mdi-cash-check', tab: 0, filter: 'active' },
        { label: 'En prueba', value: s.trial ?? 0, color: 'blue', icon: 'mdi-timer-sand', tab: 0, filter: 'trial' },
        { label: 'Vencidas', value: s.expired ?? 0, color: 'red', icon: 'mdi-alert-circle-outline', tab: 0, filter: 'expired' },
        { label: 'Usuarios', value: s.total_users ?? 0, color: 'indigo', icon: 'mdi-account-group', tab: 1, filter: 'all' },
        { label: 'Por validar', value: this.pendingCount, color: this.pendingCount ? 'orange' : 'grey', icon: 'mdi-bank-transfer-in', tab: 2 },
      ]
    },

    bizCounts() {
      const c = { all: this.businesses.length, active: 0, trial: 0, expired: 0, nosub: 0, empty: 0 }
      this.businesses.forEach(b => {
        if (b.status === 'ACTIVE') c.active++
        if (b.status === 'TRIAL') c.trial++
        if (b.status === 'EXPIRED' || b.status === 'CANCELED' || b.status === 'PAST_DUE') c.expired++
        if (!b.subscription_id) c.nosub++
        if (b.is_empty) c.empty++
      })
      return c
    },

    filteredBusinesses() {
      // Búsqueda propia en vez del :search de v-data-table, que no entra bien en
      // el objeto `owner` (empresa/dueño/correo son campos anidados).
      const q = (this.search || '').trim().toLowerCase()
      return this.businesses.filter(b => {
        if (q) {
          const heno = [b.business_name, b.owner && b.owner.name, b.owner && b.owner.email, b.plan]
            .filter(Boolean).join(' ').toLowerCase()
          if (!heno.includes(q)) return false
        }
        switch (this.bizFilter) {
          case 'active': return b.status === 'ACTIVE'
          case 'trial': return b.status === 'TRIAL'
          case 'expired': return ['EXPIRED', 'CANCELED', 'PAST_DUE'].includes(b.status)
          case 'nosub': return !b.subscription_id
          case 'empty': return !!b.is_empty
          default: return true
        }
      })
    },

    bizEmptyText() {
      if (this.businesses.length === 0) return 'Aún no hay empresas registradas'
      return 'Ninguna empresa coincide con la búsqueda o el filtro'
    },

    filteredUsers() {
      const q = (this.userSearch || '').trim().toLowerCase()
      return this.users.filter(u => {
        if (q) {
          const heno = [u.full_name, u.email, ...(u.businesses || []).map(b => b.name)]
            .filter(Boolean).join(' ').toLowerCase()
          if (!heno.includes(q)) return false
        }
        switch (this.userFilter) {
          case 'active': return u.is_active
          case 'inactive': return !u.is_active
          case 'staff': return u.is_staff
          case 'noBusiness': return !u.businesses || u.businesses.length === 0
          default: return true
        }
      })
    },
  },
  methods: {
    ...mapActions('admin', [
      'getOverview', 'markPaid', 'extendSubscription', 'suspendSubscription',
      'activateSubscription', 'setPlan', 'createSubscription', 'deleteBusiness',
      'getUsers', 'createUser', 'updateUser', 'resetUserPassword',
      'getPendingTransfers', 'approveTransfer', 'rejectTransfer',
    ]),

    esMiUsuario(item) {
      if (this.myUserId && item.id === this.myUserId) return true
      return !!this.myEmail && (item.email || '').toLowerCase() === this.myEmail
    },
    formatClp(n) { return n ? '$' + Number(n).toLocaleString('es-CL') : '$0' },
    fmtDate(d) { return d ? new Date(d).toLocaleDateString('es-CL') : 'nunca' },
    statusColor(s) {
      return { TRIAL: 'blue', ACTIVE: 'green', PAST_DUE: 'orange', EXPIRED: 'red', CANCELED: 'grey' }[s] || 'grey'
    },
    pendingColor(s) {
      return { AWAITING_TRANSFER: 'orange', PAID: 'green', REJECTED: 'red' }[s] || 'grey'
    },
    planLabel(p) { return `${p.name} · ${this.formatClp(p.price_clp)} / ${p.duration_days} días` },

    goTab(tab, filter) {
      this.tab = tab
      if (tab === 0 && filter) this.bizFilter = filter
      if (tab === 1 && this.users.length === 0) this.loadUsers()
      if (tab === 2) this.loadPending()
    },
    reloadAll() {
      this.load()
      if (this.users.length) this.loadUsers()
      if (this.tab === 2) this.loadPending()
    },

    async load() {
      this.loading = true
      const res = await this.getOverview()
      this.loading = false
      if (res && res.status === 200) {
        this.businesses = res.data.businesses
        this.plans = res.data.plans
        this.stats = res.data.stats
      } else { this.$toast.error('No se pudo cargar la administración') }
    },
    async loadUsers() {
      this.loadingUsers = true
      const res = await this.getUsers()
      this.loadingUsers = false
      if (res && res.status === 200) { this.users = res.data }
      else { this.$toast.error('No se pudieron cargar los usuarios') }
    },
    async loadPending() {
      this.loadingPending = true
      const res = await this.getPendingTransfers({ all: this.pendingShowAll })
      this.loadingPending = false
      if (res && res.status === 200) { this.pending = res.data }
      else { this.$toast.error('No se pudieron cargar los pagos') }
    },

    // ---------------------------- Empresas ----------------------------
    // Estas acciones mueven plata o cortan el acceso de un cliente: todas
    // confirman antes. Antes se ejecutaban al primer clic.
    async confirmAction(kind, item) {
      const textos = {
        markPaid: {
          titulo: 'Marcar como pagado',
          msg: `Se registra un pago manual de <b>${item.business_name}</b> y se activan 30 días. Hazlo solo si la plata ya está en la cuenta.`,
          ok: 'Marcar pagado',
        },
        extend: {
          titulo: 'Extender 30 días',
          msg: `Se extienden 30 días de <b>${item.business_name}</b> sin registrar pago.`,
          ok: 'Extender',
        },
        suspend: {
          titulo: 'Suspender acceso',
          msg: `<b>${item.business_name}</b> perderá el acceso a la app de inmediato. Sus datos no se borran.`,
          ok: 'Suspender',
        },
        activate: {
          titulo: 'Reactivar',
          msg: `Se reactiva el acceso de <b>${item.business_name}</b>.`,
          ok: 'Reactivar',
        },
      }
      const t = textos[kind]
      const ok = await this.$refs.confirm.open(t.titulo, t.msg, { okTitle: t.ok, width: 470 })
      if (!ok) return

      const map = {
        markPaid: () => this.markPaid({ subscriptionId: item.subscription_id }),
        extend: () => this.extendSubscription({ subscriptionId: item.subscription_id, days: 30 }),
        suspend: () => this.suspendSubscription({ subscriptionId: item.subscription_id }),
        activate: () => this.activateSubscription({ subscriptionId: item.subscription_id }),
      }
      this.acting = true
      const res = await map[kind]()
      this.acting = false
      if (res && res.status === 200) { this.$toast.success('Listo'); await this.load() }
      else { this.$toast.error(this.errMsg(res, 'No se pudo completar la acción')) }
    },

    openPlanDialog(item, isNew) {
      this.selected = item
      this.planIsNew = !!isNew
      this.selectedPlanId = item.plan_id || (this.plans[0] && this.plans[0].id) || null
      this.planDialog = true
    },
    async confirmSetPlan() {
      this.acting = true
      const res = this.planIsNew
        ? await this.createSubscription({ businessId: this.selected.business_id, planId: this.selectedPlanId })
        : await this.setPlan({ subscriptionId: this.selected.subscription_id, planId: this.selectedPlanId })
      this.acting = false
      if (res && (res.status === 200 || res.status === 201)) {
        this.planDialog = false
        this.$toast.success(this.planIsNew ? 'Suscripción creada' : 'Plan actualizado')
        await this.load()
      } else { this.$toast.error(this.errMsg(res, 'No se pudo guardar el plan')) }
    },

    openDeleteDialog(item) {
      this.selected = item
      this.deleteConfirmName = ''
      this.deleteError = ''
      this.deleteDialog = true
    },
    async confirmDelete() {
      this.deleteError = ''
      this.acting = true
      const res = await this.deleteBusiness({
        businessId: this.selected.business_id,
        confirmName: this.deleteConfirmName,
      })
      this.acting = false
      if (res && res.status === 200) {
        this.deleteDialog = false
        this.$toast.success(`"${this.selected.business_name}" eliminada`)
        await this.load()
        if (this.users.length) await this.loadUsers()
      } else {
        this.deleteError = this.errMsg(res, 'No se pudo eliminar')
      }
    },

    // ---------------------------- Pagos ----------------------------
    async confirmApprove(item) {
      const ok = await this.$refs.confirm.open(
        'Validar pago',
        `Confirma que recibiste <b>${this.formatClp(item.amount_clp)}</b> de <b>${item.business_name || item.email}</b>.<br><br>` +
        'Al validar se crea o extiende la cuenta y se activa la suscripción.',
        { okTitle: 'Sí, recibí el pago', width: 480 },
      )
      if (!ok) return
      this.acting = true
      const res = await this.approveTransfer({ id: item.id })
      this.acting = false
      if (res && res.status === 200) {
        this.$toast.success('Pago validado y cuenta activada')
        await this.loadPending()
        await this.load()
      } else { this.$toast.error(this.errMsg(res, 'No se pudo validar el pago')) }
    },
    openRejectDialog(item) {
      this.selectedPending = item
      this.rejectReason = ''
      this.rejectDialog = true
    },
    async confirmReject() {
      this.acting = true
      const res = await this.rejectTransfer({ id: this.selectedPending.id, reason: this.rejectReason })
      this.acting = false
      if (res && res.status === 200) {
        this.rejectDialog = false
        this.$toast.success('Pago rechazado')
        await this.loadPending()
        await this.load()
      } else { this.$toast.error(this.errMsg(res, 'No se pudo rechazar')) }
    },

    // ---------------------------- Usuarios ----------------------------
    openCreateUser() {
      this.editingUser = null
      this.userForm = { first_name: '', last_name: '', email: '', business_name: '' }
      this.userError = ''
      this.userDialog = true
    },
    openEditUser(item) {
      this.editingUser = item
      this.userForm = {
        first_name: item.first_name, last_name: item.last_name,
        email: item.email, business_name: '',
      }
      this.userError = ''
      this.userDialog = true
    },
    async saveUser() {
      this.userError = ''
      this.acting = true
      const res = this.editingUser
        ? await this.updateUser({
          id: this.editingUser.id, first_name: this.userForm.first_name,
          last_name: this.userForm.last_name, email: this.userForm.email,
        })
        : await this.createUser({
          email: this.userForm.email, first_name: this.userForm.first_name,
          last_name: this.userForm.last_name, business_name: this.userForm.business_name,
        })
      this.acting = false
      if (res && (res.status === 200 || res.status === 201)) {
        this.userDialog = false
        await this.loadUsers()
        await this.load()
        if (res.status === 201 && res.data.password) {
          this.pwData = { email: res.data.email, password: res.data.password }
          this.pwDialog = true
        } else { this.$toast.success('Usuario actualizado') }
      } else {
        this.userError = this.errMsg(res, 'No se pudo guardar')
      }
    },
    async confirmToggleActive(item, active) {
      if (!active) {
        const ok = await this.$refs.confirm.open(
          'Desactivar usuario',
          `<b>${item.full_name || item.email}</b> no podrá entrar a la app. Sus datos y su empresa no se borran.`,
          { okTitle: 'Desactivar', width: 460 },
        )
        if (!ok) return
      }
      this.acting = true
      const res = await this.updateUser({ id: item.id, is_active: active })
      this.acting = false
      if (res && res.status === 200) {
        this.$toast.success(active ? 'Usuario activado' : 'Usuario desactivado')
        await this.loadUsers()
      } else { this.$toast.error(this.errMsg(res, 'No se pudo cambiar el estado')) }
    },
    async confirmStaff(item, isStaff) {
      const ok = await this.$refs.confirm.open(
        isStaff ? 'Dar acceso de administrador' : 'Quitar acceso de administrador',
        isStaff
          ? `<b>${item.full_name || item.email}</b> podrá ver y modificar TODAS las empresas, usuarios y pagos.`
          : `<b>${item.full_name || item.email}</b> dejará de tener acceso a este panel.`,
        { okTitle: isStaff ? 'Dar acceso' : 'Quitar acceso', width: 470 },
      )
      if (!ok) return
      this.acting = true
      const res = await this.updateUser({ id: item.id, is_staff: isStaff })
      this.acting = false
      if (res && res.status === 200) {
        this.$toast.success(isStaff ? 'Ahora es administrador' : 'Acceso de administrador quitado')
        await this.loadUsers()
      } else { this.$toast.error(this.errMsg(res, 'No se pudo cambiar el permiso')) }
    },
    async doResetPassword(item) {
      const ok = await this.$refs.confirm.open(
        'Resetear contraseña',
        `Se genera una contraseña nueva para <b>${item.full_name || item.email}</b> y la anterior deja de servir.`,
        { okTitle: 'Generar', width: 460 },
      )
      if (!ok) return
      this.acting = true
      const res = await this.resetUserPassword({ id: item.id })
      this.acting = false
      if (res && res.status === 200 && res.data.password) {
        this.pwData = { email: res.data.email, password: res.data.password }
        this.pwDialog = true
      } else { this.$toast.error(this.errMsg(res, 'No se pudo resetear la contraseña')) }
    },
    copyPw() {
      navigator.clipboard && navigator.clipboard.writeText(this.pwData.password)
      this.$toast && this.$toast.success('Contraseña copiada')
    },

    errMsg(res, fallback) {
      return (res && res.data && (res.data.detail || res.data.error)) || fallback
    },
  },
  watch: {
    // Antes solo cargaba si users.length === 0, así que después de borrar todo
    // no volvía a consultar.
    tab(v) {
      if (v === 1) this.loadUsers()
      if (v === 2) this.loadPending()
    },
  },
  created() { this.load() },
}
</script>

<style scoped>
.card-clickable { cursor: pointer; transition: box-shadow .15s ease; }
.card-clickable:hover { box-shadow: 0 2px 10px rgba(0, 0, 0, .10) !important; }
.h-100 { height: 100%; }
</style>
